import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {AgentIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {type FC, useCallback, useState} from 'react'
import type {FindingEntity} from './types'

interface FixFindingResponse {
  task_id: string
  task_url: string
}

const fixFinding = async (basePath: string, finding: FindingEntity): Promise<FixFindingResponse> => {
  const response = await verifiedFetchJSON(`${basePath}/code_review_findings/fix`, {
    method: 'POST',
    body: {entity_type: finding.entityType, entity_id: finding.entity.id},
  })
  if (!response.ok) {
    const body = await response.json()
    throw new Error(body.error ?? 'Failed to create fix task')
  }
  return response.json()
}

interface FindingActionsProps {
  onDismiss?: () => void
  onReopen?: () => void
  dismissed?: boolean
  basePath: string
  finding: FindingEntity
}

export const FindingActions: FC<FindingActionsProps> = ({onDismiss, onReopen, dismissed, basePath, finding}) => {
  return (
    <div className="d-flex gap-2">
      <FixActionButton basePath={basePath} finding={finding} />
      {dismissed ? <Button onClick={onReopen}>Reopen</Button> : <Button onClick={onDismiss}>Dismiss</Button>}
    </div>
  )
}

interface FixActionButtonProps {
  basePath: string
  finding: FindingEntity
}

const FixActionButton: FC<FixActionButtonProps> = ({basePath, finding}) => {
  const [isFixing, setIsFixing] = useState(false)
  const [taskUrl, setTaskUrl] = useState<string | null>(null)

  const handleFix = useCallback(async () => {
    setIsFixing(true)
    try {
      const result = await fixFinding(basePath, finding)
      setTaskUrl(result.task_url)
    } finally {
      setIsFixing(false)
    }
  }, [basePath, finding])

  if (taskUrl) {
    return (
      <Button as="a" href={taskUrl} leadingVisual={AgentIcon}>
        View Copilot task
      </Button>
    )
  }

  return (
    <Button onClick={handleFix} disabled={isFixing} leadingVisual={AgentIcon}>
      {isFixing ? 'Fixing…' : 'Fix with Copilot'}
    </Button>
  )
}
